import { useEffect, useState, type ReactElement } from 'react';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';

export interface AnalyticsSettingsValues {
  googleTagManagerId: string;
  googleMeasurementId: string;
}

interface AnalyticsSettingsModalProps {
  open: boolean;
  initialValues: AnalyticsSettingsValues;
  isSaving: boolean;
  onClose: () => void;
  onSave: (values: AnalyticsSettingsValues) => void;
}

export function AnalyticsSettingsModal({
  open,
  initialValues,
  isSaving,
  onClose,
  onSave,
}: AnalyticsSettingsModalProps): ReactElement {
  const [googleTagManagerId, setGoogleTagManagerId] = useState(initialValues.googleTagManagerId);
  const [googleMeasurementId, setGoogleMeasurementId] = useState(initialValues.googleMeasurementId);

  useEffect(() => {
    if (open) {
      setGoogleTagManagerId(initialValues.googleTagManagerId);
      setGoogleMeasurementId(initialValues.googleMeasurementId);
    }
  }, [open, initialValues.googleTagManagerId, initialValues.googleMeasurementId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className="vtwo:flex vtwo:items-center vtwo:justify-between vtwo:font-bold">
        Analytics
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box className="vtwo:flex vtwo:flex-col vtwo:gap-4 vtwo:mt-2">
          <Box>
            <Typography className="vtwo:text-typography-primary vtwo:mb-1">
              Google Tag Manager ID
            </Typography>
            <TextField
              autoFocus
              fullWidth
              placeholder="Google Tag Manager ID"
              value={googleTagManagerId}
              onChange={(e) => setGoogleTagManagerId(e.target.value)}
            />
          </Box>
          <Box>
            <Typography className="vtwo:text-typography-primary vtwo:mb-1">
              Google Measurement ID
            </Typography>
            <TextField
              fullWidth
              placeholder="Google Measurement ID"
              value={googleMeasurementId}
              onChange={(e) => setGoogleMeasurementId(e.target.value)}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions className="vtwo:px-6 vtwo:pb-6 vtwo:gap-3">
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isSaving}
          className="vtwo:flex-1 vtwo:text-typography-primary vtwo:border-plus-border"
          sx={{ borderRadius: '8px' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() =>
            onSave({
              googleTagManagerId: googleTagManagerId.trim(),
              googleMeasurementId: googleMeasurementId.trim(),
            })
          }
          disabled={isSaving}
          className="vtwo:flex-1"
          sx={{
            borderRadius: '8px',
            backgroundColor: '#327D5D',
            '&:hover': { backgroundColor: '#28644A' },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
